import AButton from './AButton.js';
import AContainer from './AContainer.js';
import AText from './AText.js';
import Utils from '../Utils.js';
import NineSlice from '../NineSlice.js';
import AImage from './AImage.js';
import InteractableObject from '../InteractableObject.js';
import ASpine from './ASpine.js';
import AEmitter from './AEmitter.js';
import ASprite from './ASprite.js';
import AMatterSprite from './AMatterSprite.js';

export default class AScene extends Phaser.Scene
{
    constructor(sceneName)
    {
        super(sceneName);

        this.defaultAtlas = 'btf';
        this.defaultAudioSprite = 'gameAudio';
    }

    preload()
    {
        Utils.CurrentScene = this;
        this.shadowObjects = [];
    }

    /**
     * @return {ASpine} 
     */
    addSpine(x, y, key, animation, loop = false, onComplete = null)
    {
        let spine = new ASpine(this, x, y, key, animation, loop, onComplete);
        return spine;
    }

    /**
     * @returns {AEmitter}
     */
    addEmitter(properties, frame = null, atlas = null, parent = null)
    {
        let emitter = new AEmitter(this, properties, frame, atlas);
        emitter.scaleX.onUpdate = emitter.scaleX.defaultUpdate;
        this.addToParent(emitter.manager, parent);
        return emitter;

    }

    /**
     * Adds a Mask
     * @return {Phaser.GameObjects.Graphics} 
     */
    addSquareMask(child, x, y, width, height, debug)
    {
        let shape = this.addRect(x, y, width, height, 0xff0).setVisible(debug);
        let mask = new Phaser.Display.Masks.GeometryMask(this, shape);
        if(child instanceof Array) for(let i = 0; i < child.length; i++) child[i].mask = mask; 
        else child.mask = mask; 
        return shape;
    }

    /**
     * Adds a Mask.
     * @return {Phaser.GameObjects.Graphics} 
     */
    addPathMask(child, x, y, points, debug = false)
    {
        let graphics = this.addPolygon(x, y, points, 0xff0000);
        graphics.visible = debug;
        this.add.existing(graphics);
        if(debug) graphics.setDepth(10000);

        if(child)
        {
            if (child instanceof Array) child.forEach((kid) => { kid.mask = new Phaser.Display.Masks.BitmapMask(this, graphics); });
            else child.mask = new Phaser.Display.Masks.BitmapMask(this, graphics);
        }

        return graphics;
    }

    /**
     * @return {Phaser.GameObjects.Graphics} 
     */
    addPolygon(x, y, points, color = 0xffffff, parent = null)
    {
        let poly = new Phaser.Geom.Polygon(points);

        let graphics = this.make.graphics();
        graphics.points = poly.points;
        graphics.x = x;
        graphics.y = y;
        graphics.fillStyle(color);
        graphics.fillPoints(poly.points);

        this.addToParent(graphics, parent);
        return graphics;
    }

    /**
     * @return {Phaser.GameObjects.Graphics} 
     */
    addSlice(x, y, radius, startAngle, endAngle, clockWise = true, color = 0xffffff, parent = null)
    {
        let graphics = this.make.graphics();
        graphics.fillStyle(color, 1);
        graphics.slice(x, y, radius, Phaser.Math.DegToRad(startAngle), Phaser.Math.DegToRad(endAngle), !clockWise);
        graphics.fillPath();

        this.addToParent(graphics, parent);
        return graphics;
    }

    /**
     * @return {AButton}
     */
    addButton(x, y, frameUp, onClick, parent = null, atlas = this.defaultAtlas)
    {
        let button = new AButton(this, x, y, atlas, frameUp, onClick);
        this.addToParent(button, parent);
        return button;
    }

     /**
     * @return {NineSlice}
     */
    addNineSlice(x, y, frame, top, bot, left, right, width = -1, height = -1, parent = null, graphic = this.defaultAtlas)
    {
        let nineSlice = new NineSlice(this, graphic, frame, top, bot, left, right, width, height);
        nineSlice.setPosition(x, y);
        this.addToParent(nineSlice, parent);
        return nineSlice;
    }

    addDropShadow(toImage, xOffset = 8, yOffset = 8, texture = null)
    {
        Utils.TopParent(toImage).setDepth(1);
        let stagePos = Utils.StagePos(toImage);
        let shadow = null;
        
        if(toImage instanceof AMatterSprite)
        {
            shadow = this.addSprite(stagePos.x + xOffset, stagePos.y + yOffset, toImage.texture.key);
        }else
        {
            shadow = this.addImage(stagePos.x + xOffset, stagePos.y + yOffset, toImage.frame.name, null, (texture == null ? this.defaultAtlas : texture));
        }
        shadow.setScale(toImage.scaleX);
        shadow.angle = toImage.angle;
        shadow.tint = 0x000000;
        shadow.alpha = .3;

        toImage.shadow = shadow;
        toImage.shadow.xOffset = xOffset;
        toImage.shadow.yOffset = yOffset;

        this.shadowObjects.push(toImage);

        return toImage;
    }

    /**
     * @param {AContainer} container 
     * @param {AContainer} parent 
     * @return {AContainer}
     */
    addContainer(x, y, container, parent = null)
    {
        if(container == null) container = new Phaser.GameObjects.Container(this, 0, 0);
        let cont = container;
        cont.setPosition(x, y);
        this.addToParent(cont, parent);
        return cont;
    }

    addText(x, y, textSize, hexColor, font, text = "", parent = null, alignMiddle = true)
    {
        let textField = new AText(this, x, y, textSize, hexColor, font, text);
        if(alignMiddle)
        {
            textField.setOrigin(.5);
            textField.setAlign('center');
        }
        this.addToParent(textField, parent);
        return textField;
    }

    /** 
     * @return {AImage}
     */
    addImage(x, y, key, parent = null, atlas = this.defaultAtlas)
    {
        let image = new AImage(this, x, y, atlas, key, null);
        this.addToParent(image, parent);
        return image;
    }

    /** 
     * @return {InteractableObject}
     */
    addInteractableObject(x, y, key, origin = .5, parent = null, activeCoords)
    {
        let image = new InteractableObject(this, x, y, this.defaultAtlas, key, origin, activeCoords);
        this.addToParent(image, parent);
        return image;
    }

    addDelay(delay, func)
    {
        return this.time.delayedCall(delay, func);
    }

    addRepeatingEvent(delay, event, timesToRepeat = -1)
    {
        return this.time.addEvent({ delay: delay, callback: event, repeat: timesToRepeat });
    }

    /**
     * @param {Phaser.Math.Easing} ease 
     * @param {Phaser.GameObjects.GameObject} object 
     * @return {Phaser.Tweens.Tween}
     */
    addTween(object, duration, parameters, ease = null, onComplete = null)
    {
        if(object == null) return null;

        parameters.targets = object;
        parameters.ease = ease;
        parameters.duration = duration;
        parameters.onComplete = onComplete;

        if(parameters.onStart && parameters.delay)
        {
            let startFunc = parameters.onStart;
            delete parameters.onStart;
            this.addDelay(parameters.delay, startFunc);
        }
        
        let tween = this.tweens.add(parameters);

        return tween;
    }


    /**
     * Moves object along a path. You can specify segments in the parameters options like so:
     * ```segments: [
     *      {ease: Phaser.Math.Easing.Sine.In, normDuration: .5, timeScale: 3}, 
     *      {ease: Phaser.Math.Easing.Bounce.Out}
     * ]```
     */
    addPathTween(object, path, duration, parameters = {}, ease = null, onComplete = null)
    {
        let follower = { t: 0, vec: new Phaser.Math.Vector2() };

        parameters.ease = parameters.easeSegments ? Phaser.Math.Easing.Linear.Linear : ease;
        parameters.duration = duration;
        parameters.onComplete = onComplete;
        parameters.t = 1;
        parameters.targets = follower;
        let existingUpdate = parameters.onUpdate;
        let segIdx = 0;
        let totalTime = 0;
        if(parameters.segments) parameters.segments.forEach((seg)=>{
            if(seg.normDuration) totalTime += seg.normDuration;
            else seg.normDuration = 1-totalTime;
        })
        parameters.onUpdate = ()=>{
            if(existingUpdate) existingUpdate();
            let pointPos = follower.t;

            if(parameters.segments) 
            {
                let segments = parameters.segments;
                let tweenTime = follower.t;
                let nextIdxTime = 0;

                for (let i = 0; i <= segIdx; i++) nextIdxTime += segments[i].normDuration;
                while (segments[segIdx] && tweenTime > nextIdxTime) {
                    segIdx++;
                    nextIdxTime += segments[segIdx].normDuration;
                }

                let seg = segments[segIdx];
                let segTime = seg.normDuration;
                let previousTime = nextIdxTime - segTime;
                let normalizedSegTime = (tweenTime - previousTime) / segTime;
                let normalizedEasedTime = seg.ease ? seg.ease(normalizedSegTime) * segTime : normalizedSegTime * segTime;
                
                pointPos = previousTime + normalizedEasedTime;
                pathTween.timeScale = seg.timeScale ? seg.timeScale : 1;
            }

            path.getPoint(pointPos, follower.vec);
            object.setPosition(follower.vec.x, follower.vec.y);
        };

        let pathTween = this.tweens.add(parameters);
        return pathTween;
    }

    addSprite(x, y, animation, parent = null)
    {
        if(this.cache.game.anims.anims.entries[animation] == null)
        {
            console.warn("The animation: \"" + animation + "\" does not exist.\nAnims available: " , Object.keys(this.cache.game.anims.anims.entries));
            return null;
        }
        let sprite = new ASprite(this, x, y, animation);
        this.addToParent(sprite, parent);
        return sprite;
    }

    killTween(object)
    {
        this.tweens.killTweensOf(object);
    }

    /**
     * @return {Phaser.GameObjects.Graphics} 
     */
    addCirc(x, y, radius, color, parent = null)
    {
        let circ = this.add.graphics({ fillStyle: { color: color } });
        circ.fillCircleShape(new Phaser.Geom.Circle(x, y, radius));
        this.addToParent(circ, parent);
        return circ;
    }

    addEllipse(x, y, width, height, color, parent = null)
    {
        let ell = this.add.graphics({ fillStyle: { color: color } });
        ell.fillEllipseShape(new Phaser.Geom.Ellipse(0, -height * .5, width, height));
        ell.setPosition(x, y);
        this.addToParent(ell, parent);
        return ell;
    }

    /**
     * @return {Phaser.GameObjects.Graphics} 
     */
    addRect(x, y, width, height, color, parent = null, setInteractive = false, listener = null, event = "pointerdown", isVisible = false)
    {
        let rec = this.add.graphics({ fillStyle: { color: color }});
        this.addToParent(rec, parent);
        rec.fillRectShape(new Phaser.Geom.Rectangle(0, 0, width, height));
        rec.width = width;
        rec.height = height;
        rec.setPosition(x, y);

        if(setInteractive)
        {
            rec.alpha = isVisible ? 1 : .000001;
            rec.setInteractive(new Phaser.Geom.Rectangle(0, 0, width, height), Phaser.Geom.Rectangle.Contains);
            rec.on(event, listener);
        }
        
        return rec;
    }

    addRoundedRect(x, y, width, height, color, radius, parent, setInteractive = false, onEvent = null, eventListener = "pointerdown", isVisible = false)
    {
        let graphics = this.add.graphics();
        this.addToParent(graphics, parent);
        graphics.fillStyle(color, 1);
        graphics.fillRoundedRect(x - (width * .5), y - (height * .5), width, height, {tl: 0, tr: 0, bl: radius, br: radius });
        graphics.width = width;
        graphics.height = height;
        if(setInteractive)
        {
            graphics.alpha = isVisible ? 1 : .000001;
            graphics.setInteractive(new Phaser.Geom.Rectangle(x - (width * .5), y - (height * .5), width, height), Phaser.Geom.Rectangle.Contains);
            graphics.on(eventListener, onEvent);
        }
        return graphics;
    }

    addToParent(object, parent) 
    {
        if(object instanceof Phaser.GameObjects.Particles.ParticleEmitterManager && parent instanceof Phaser.GameObjects.Container)
        {
            parent.add(object);
            return;
        }
        if(!parent || parent == this) this.add.existing(object);
        else parent.add(object);
    }

    get size()
    {
        return {width: this.game.config.width, height: this.game.config.height};
    }

    get mid()
    {
        return {x: this.game.config.width * .5, y: this.game.config.height * .5};
    }

    /**
     * @return {Phaser.Sound.HTML5AudioSound}
     */
    playSound(sound, volume = 1, repeat = false)
    {
        let gameAudio = this.sound.addAudioSprite(this.defaultAudioSprite);

        gameAudio.play(sound);
        gameAudio.volume = volume;
        gameAudio.loop = repeat;

        return gameAudio;
    }

    stopSounds()
    {
        this.sound.stopAll();
    }

    update()
    {
        this.shadowObjects.forEach((shadowObj)=>{
            let stagePos = Utils.StagePos(shadowObj);
            let parent = Utils.TopParent(shadowObj);
            let shadow = shadowObj.shadow;
            shadow.setScale(shadowObj.scaleX);
            shadow.setPosition(stagePos.x + shadow.xOffset, stagePos.y + shadow.yOffset);
            shadow.angle = shadowObj.angle + parent.angle;
            shadow.visible = shadowObj.visible;
        });
    }
}